import {
    Addon,
    RemovedIngredient,
} from '@/features/common/components/Overview/parts/ProductItem/types'
import {
    formatAddonName,
    formatProductItemName,
} from '@/features/common/components/Overview/utils/formats'
import {
    CartContainerItemFragment,
    CartItemAddInput,
    CartItemAddedProductVariantFragment,
    CartItemFragment,
    CartItemToppingFragment,
    CartItemUpdateInput,
    RecipeIngredientFragment,
} from '@/features/graphql/generated/graphql'
import { ContainerItem } from '@/features/pos/components/Cart/parts/Overview/parts/CartContainerItem/types'
import {
    AutomaticContainer,
    AutomaticContainerSource,
    CartProductItem,
} from '@/features/pos/components/Cart/parts/Overview/parts/CartItem/types'
import { aggregateAddonGroupsToCartItemToppingsAndAdditionProductVariants } from '@/features/pos/components/Cart/utils/aggregateAddonGroupsToCartItemToppingsAndAdditionProductVariants'
import { getAutomaticContainerId } from '@/features/pos/components/Cart/utils/automatic-containers'
import { formatProductName } from '@/features/pos/components/Cart/utils/formats'
import { Form } from '@/features/pos/components/ProductConfigurationModal/form/types'
import { calculateTotalPriceFromUnitPrice } from '@/utils/calculateTotalPriceFromUnitPrice'
import { formatFreeOfChargeAblePrice } from '@/utils/formatFreeOfChargeAblePrice'
import { TFunction } from 'i18next'

export function transformFormProductToCartItem(product: Form): CartItemAddInput
export function transformFormProductToCartItem(
    product: Form,
    cartItemId: string
): CartItemUpdateInput
export function transformFormProductToCartItem(
    product: Form,
    cartItemId?: string
): CartItemAddInput | CartItemUpdateInput {
    return {
        productVariantUuid: product.productVariantId,
        ...aggregateAddonGroupsToCartItemToppingsAndAdditionProductVariants(product.addonGroups),
        ...(product.removedIngredients && {
            removedRecipeIngredients: Object.keys(product.removedIngredients).filter(
                (id) => product.removedIngredients![id] === true
            ),
        }),
        ...(cartItemId ? { cartItemUuid: cartItemId } : { quantity: product.quantity }),
    }
}

const transformCartItemToppingToAddon = (
    itemTopping: CartItemToppingFragment,
    t: TFunction<'app'>
): Addon => {
    const unitPrice = itemTopping.toppingGroup.freeOfCharge
        ? { ...itemTopping.topping.price, amount: '0' }
        : itemTopping.topping.price

    return {
        id: itemTopping.topping.uuid,
        name: formatAddonName(
            itemTopping.topping.name,
            itemTopping.quantity,
            formatFreeOfChargeAblePrice(unitPrice, t)
        ),
    }
}

const transformCartItemAddedProductVariantToAddon = (
    itemProductVariant: CartItemAddedProductVariantFragment,
    t: TFunction<'app'>
): Addon => {
    const unitPrice = itemProductVariant.additionGroup.freeOfCharge
        ? { ...itemProductVariant.productVariant.price, amount: '0' }
        : itemProductVariant.productVariant.price

    return {
        id: itemProductVariant.productVariant.uuid,
        name: formatAddonName(
            formatProductName(
                itemProductVariant.productVariant.product.name,
                itemProductVariant.productVariant.name,
                'parentheses'
            ),
            itemProductVariant.quantity,
            formatFreeOfChargeAblePrice(unitPrice, t)
        ),
    }
}

const transformRecipeToRemovedIngredient = (
    recipeIngredient: RecipeIngredientFragment
): RemovedIngredient => ({
    id: recipeIngredient.uuid,
    name: recipeIngredient.name,
})

type ProductVariantContainer = NonNullable<CartItemFragment['productVariant']['container']>

const transformProductVariantContainerToAutomaticContainer = (
    container: ProductVariantContainer,
    source: AutomaticContainerSource,
    quantityPerMainItem: number,
    totalQuantity: number,
    t: TFunction<'app'>,
    freeOfCharge = false
): AutomaticContainer => {
    const unitPrice = freeOfCharge ? { ...container.price, amount: '0' } : container.price

    return {
        id: getAutomaticContainerId(source),
        name: formatAddonName(
            container.internalName,
            quantityPerMainItem,
            formatFreeOfChargeAblePrice(unitPrice, t)
        ),
        price: formatFreeOfChargeAblePrice(
            calculateTotalPriceFromUnitPrice(unitPrice, totalQuantity),
            t,
            false,
            2
        ),
        source,
    }
}

export const transformCartItemToAutomaticContainers = (
    item: CartItemFragment,
    t: TFunction<'app'>
): AutomaticContainer[] => {
    const containers: AutomaticContainer[] = []

    if (item.productVariant.container) {
        containers.push(
            transformProductVariantContainerToAutomaticContainer(
                item.productVariant.container,
                { cartItemUuid: item.uuid },
                1,
                item.quantity,
                t
            )
        )
    }

    item.additionGroupProductVariants.forEach((addedProductVariant) => {
        if (!addedProductVariant.productVariant.container) return

        containers.push(
            transformProductVariantContainerToAutomaticContainer(
                addedProductVariant.productVariant.container,
                { cartItemAddedProductVariantUuid: addedProductVariant.uuid },
                addedProductVariant.quantity,
                addedProductVariant.quantity * item.quantity,
                t,
                addedProductVariant.additionGroup.freeOfCharge
            )
        )
    })

    return containers
}

export const transformCartItemToProductItem = (
    item: CartItemFragment,
    t: TFunction<'app'>
): CartProductItem => ({
    id: item.uuid,
    name: formatProductItemName(
        formatProductName(item.productVariant.product.name, item.productVariant.name),
        formatFreeOfChargeAblePrice(item.productVariant.price, t)
    ),
    quantity: item.quantity,
    price: formatFreeOfChargeAblePrice(item.totalProductPrice, t, false, 2),
    addons: [
        ...item.toppingGroupToppings.map((topping) => transformCartItemToppingToAddon(topping, t)),
        ...item.additionGroupProductVariants.map((productVariant) =>
            transformCartItemAddedProductVariantToAddon(productVariant, t)
        ),
    ],
    removedIngredients: item.removedRecipeIngredients.map(transformRecipeToRemovedIngredient),
    containers: transformCartItemToAutomaticContainers(item, t),
})

export const transformCartContainerItemToContainerItem = (
    item: CartContainerItemFragment,
    t: TFunction<'app'>
): ContainerItem => ({
    id: item.uuid,
    name: item.container.internalName,
    quantity: item.quantity,
    unitPrice: formatFreeOfChargeAblePrice(item.container.price, t),
    price: formatFreeOfChargeAblePrice(item.totalPrice, t, false, 2),
})
